import { useEffect } from 'react';
import { loadTransactions, POSSettings } from '@/lib/posStorage';
import { formatFiat, formatBCH } from '@/lib/bch';

const LAST_SUMMARY_KEY = 'bch-pos:last-summary-date';

function todayStr(): string {
  return new Date().toISOString().slice(0, 10); // "YYYY-MM-DD"
}

function yesterdayStr(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
}

function getSummaryForDate(dateStr: string) {
  const txs = loadTransactions();
  const dayTxs = txs.filter((tx) => {
    const d = new Date(tx.timestamp).toISOString().slice(0, 10);
    return d === dateStr;
  });
  const totalFiat = dayTxs.reduce((sum, tx) => sum + tx.fiatAmount, 0);
  const totalBCH = dayTxs.reduce((sum, tx) => sum + tx.bchAmount, 0);
  return { count: dayTxs.length, totalFiat, totalBCH, currency: dayTxs[0]?.currency ?? 'USD' };
}

async function requestPermissionAndNotify(title: string, body: string): Promise<void> {
  if (!('Notification' in window)) return;

  let permission = Notification.permission;
  if (permission === 'default') {
    permission = await Notification.requestPermission();
  }
  if (permission !== 'granted') return;

  // Use service worker notification if available (works when app is in background)
  if ('serviceWorker' in navigator) {
    const reg = await navigator.serviceWorker.getRegistration();
    if (reg) {
      await reg.showNotification(title, {
        body,
        icon: '/icon-192.png',
        badge: '/icon-192.png',
        tag: 'bch-pos-daily-summary',
      });
      return;
    }
  }

  // Fallback to plain Notification
  new Notification(title, { body, icon: '/icon-192.png' });
}

/**
 * Fires a daily sales summary notification once per day when the merchant
 * opens the app. Shows yesterday's totals so they know how the previous day went.
 */
export function useDailySummary(settings: POSSettings) {
  useEffect(() => {
    // Only run if merchant has opted in
    if (!settings.dailySummary) return;

    const lastSent = localStorage.getItem(LAST_SUMMARY_KEY);
    const today = todayStr();

    // Already sent today — skip
    if (lastSent === today) return;

    const yesterday = yesterdayStr();
    const summary = getSummaryForDate(yesterday);

    // Nothing sold yesterday — no need to notify
    if (summary.count === 0) {
      localStorage.setItem(LAST_SUMMARY_KEY, today);
      return;
    }

    const merchantName = settings.merchantName || 'BCH POS';
    const title = `${merchantName} — Yesterday's Sales`;
    const body = [
      `${summary.count} sale${summary.count !== 1 ? 's' : ''}`,
      `${formatFiat(summary.totalFiat, summary.currency)} total`,
      `${formatBCH(summary.totalBCH)} BCH received`,
    ].join(' · ');

    localStorage.setItem(LAST_SUMMARY_KEY, today);
    requestPermissionAndNotify(title, body);
  // Run once on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
